import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Star, CheckCircle, Wallet, Award, Lock, Cpu, Globe, MapPin,
  TrendingUp, Clock, Users, DollarSign, Heart, Zap
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead, OrganizationSchema, WebsiteSchema, ServiceSchema } from '../components/SEO';
import { SERVICES, MEMBERSHIP_PLANS } from '../data/services';
import { SEO_KEYWORDS } from '../components/SEO';

const ServiceGrid = ({ navigate }) => {
  const [highlightedId, setHighlightedId] = useState(null);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {SERVICES.map((service) => {
        const Icon = service.icon;
        const isHighlighted = highlightedId === service.id;
        return (
          <div
            key={service.id}
            onClick={() => { setHighlightedId(service.id); setTimeout(() => navigate(`/search?service=${service.id}`), 350); }}
            className={`group cursor-pointer bg-white rounded-2xl p-5 border transition-all duration-300 text-center ${
              isHighlighted 
                ? 'border-orange-500 ring-2 ring-orange-500 shadow-lg shadow-orange-500/20 scale-[1.05] bg-orange-50' 
                : 'border-gray-100 hover:border-orange-200 hover:shadow-lg'
            }`}
            data-testid={`service-card-${service.id}`}
          >
            <div className={`w-14 h-14 mx-auto mb-3 ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm mb-1">{service.name}</h3>
            <p className="text-xs text-gray-500">From Rs.1000/hr</p>
          </div>
        );
      })}
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [humanCount, setHumanCount] = useState(11247);

  useEffect(() => {
    const interval = setInterval(() => {
      setHumanCount(Math.floor(Math.random() * (12894 - 10378 + 1)) + 10378);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { name: "Priya M.", role: "Event Partner", text: "Found the perfect partner for my sister's wedding. Professional, punctual, and made the event memorable!", rating: 5 },
    { name: "Rahul K.", role: "Travel Buddy", text: "Needed someone for a solo trip to Goa. Great experience and amazing local knowledge throughout.", rating: 5 },
    { name: "Anita S.", role: "Elder Care", text: "My mother loved having someone to go to doctor visits with. Reliable and caring service.", rating: 5 }
  ];

  const providerBenefits = [
    { icon: DollarSign, title: "Earn Up To Rs.1 Lac/Month", desc: "Set your own hourly rate and earn on your schedule. Get 80% of every booking." },
    { icon: Clock, title: "Flexible Hours", desc: "Work when you want. Accept or decline bookings based on your availability." },
    { icon: Globe, title: "Pan-India Reach", desc: "Get discovered by hirers across all Indian cities and pin codes." },
    { icon: Shield, title: "Safe & Secure", desc: "SOS button, verified profiles, and secure payouts directly to your bank." },
    { icon: TrendingUp, title: "Grow Your Profile", desc: "Build reputation with ratings and reviews. Higher ratings = more bookings." },
    { icon: Award, title: "AI-Powered Matching", desc: "Our AI connects you with hirers looking for exactly your skills and location." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="HumanOnRent.AI - India's First AI-Driven Human on Rent Platform | Rent a Human for Any Occasion"
        description="Rent verified humans for any occasion across India. 1 Lac+ providers for events, travel, medical visits, elder care, shopping & 20+ services. AI-powered matching. Rs.1000/hr."
        keywords={SEO_KEYWORDS.home}
        url="https://humanonrent.ai"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <ServiceSchema services={SERVICES} />
      <span className="sr-only-seo">
        boyfriend on rent india, girlfriend on rent services, human on rent platform, 
        rent a friend india, humanonrent.ai, ai driven human rental, hire human india
      </span>

      <Header />

      {/* Hero Section - CENTERED */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-b from-orange-50/60 via-pink-50/30 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-8 border border-orange-200/60">
            <Cpu className="w-4 h-4" />
            <span>India's First AI-Driven Human on Rent Platform</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] mb-6 tracking-tight">
            Rent a Human for
            <span className="block gradient-text">Any Occasion</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            1,00,000+ registered members across all Indian cities and pin codes. 
            AI-powered matching for events, travel, medical visits, or great company.
          </p>
          
          {/* Two main CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button 
              onClick={() => navigate('/auth')}
              className="btn-gradient flex items-center justify-center gap-2 text-lg px-8 py-4 rounded-full"
              data-testid="hero-rent-human-btn"
            >
              <Users className="w-5 h-5" />
              Rent a Human
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => navigate('/auth')}
              className="bg-gray-900 text-white flex items-center justify-center gap-2 text-lg px-8 py-4 rounded-full hover:bg-gray-800 transition-all shadow-lg"
              data-testid="hero-rent-yourself-btn"
            >
              <Wallet className="w-5 h-5" />
              Rent Yourself & Earn
            </button>
          </div>

          {/* Trust Badges - centered */}
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { icon: Cpu, text: "AI-Powered" },
              { icon: Shield, text: "Verified Profiles" },
              { icon: Lock, text: "Secure Payments" },
              { icon: Globe, text: "All India Coverage" }
            ].map((badge) => (
              <div key={badge.text} className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                <badge.icon className="w-4 h-4 text-green-600" />
                <span>{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">1,00,000+</p>
              <p className="text-gray-400 text-sm sm:text-base">Registered Members</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">
                <span className="text-green-400 transition-all duration-500" data-testid="live-human-count">
                  {humanCount.toLocaleString('en-IN')}+
                </span>
              </p>
              <p className="text-green-400/70 text-sm sm:text-base flex items-center justify-center gap-1.5">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                Active Humans for Rent
              </p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">All India</p>
              <p className="text-gray-400 text-sm sm:text-base">Pin Codes & Cities</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">20+</p>
              <p className="text-gray-400 text-sm sm:text-base">Service Categories</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">AI-Driven</p>
              <p className="text-gray-400 text-sm sm:text-base">Smart Matching</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-4">How It Works</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple. <span className="gradient-text">Safe. Smart.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Choose Service", desc: "20+ categories, Rs.1000/hr + GST", color: "from-orange-500 to-pink-500" },
              { step: "02", title: "Pick Date & Hours", desc: "Select when and how long", color: "from-pink-500 to-purple-500" },
              { step: "03", title: "Find Your Match", desc: "AI matches you with the best", color: "from-purple-500 to-blue-500" },
              { step: "04", title: "Meet & Confirm", desc: "Dual confirm after service", color: "from-blue-500 to-cyan-500" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid - UNIFORM DESIGN */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-4">Our Services</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              For Every <span className="gradient-text">Occasion</span>
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">Choose from 20+ professional service categories</p>
          </div>

          <ServiceGrid navigate={navigate} />

          <div className="mt-8 text-center">
            <Link to="/services" className="text-orange-600 font-semibold hover:text-orange-700 text-sm inline-flex items-center gap-1">
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Availability Banner */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-white text-center md:text-left">
              <Globe className="w-10 h-10 hidden md:block" />
              <div>
                <h3 className="text-2xl font-bold">Available All Over India</h3>
                <p className="text-white/80">Every pin code, every city — Tier 1 & Tier 2</p>
              </div>
            </div>
            <Link to="/cities" className="bg-white text-purple-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all inline-flex items-center gap-2" data-testid="view-cities-btn">
              <MapPin className="w-4 h-4" />
              View Cities We Serve
            </Link>
          </div>
        </div>
      </section>

      {/* RENT YOURSELF - Provider Benefits */}
      <section className="section-padding bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-4">Rent Yourself</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Why Become a Provider?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Turn your time into income. Set your own rate, choose your hours, and earn up to Rs.1 Lac per month.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {providerBenefits.map((benefit) => (
              <div key={benefit.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/auth?role=provider')}
              className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-10 py-4 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-orange-500/25 transition-all"
              data-testid="become-provider-cta"
            >
              Start Earning Today
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-4">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              What Our <span className="gradient-text">Users Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="premium-card p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{t.name}</p>
                  <p className="text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Plans */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-4">Membership Plans</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Join & Earn Up To Rs.1 Lac/Month
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose a membership plan to get listed as a provider and start earning.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {Object.values(MEMBERSHIP_PLANS).map((plan) => (
              <div 
                key={plan.id}
                className={`rounded-3xl p-8 text-center ${plan.popular ? 'bg-gradient-to-b from-orange-500 to-pink-500 text-white shadow-xl shadow-orange-500/20 ring-4 ring-orange-500/20 scale-105' : 'bg-white border border-gray-200 shadow-md'}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && <div className="badge-gradient mb-4 inline-block">Most Popular</div>}
                <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>{plan.validity}</h3>
                <div className="mb-4">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>Rs.{plan.price}</span>
                  <span className={`text-sm ml-1 ${plan.popular ? 'text-white/70' : 'text-gray-500'}`}>+ GST</span>
                </div>
                <p className={`text-2xl font-semibold mb-6 ${plan.popular ? 'text-white/90' : 'text-orange-600'}`}>Total: Rs.{plan.total}</p>
                <ul className="space-y-2 mb-8 text-left">
                  {plan.features.map((feature) => (
                    <li key={feature} className={`flex items-center gap-2 text-sm ${plan.popular ? 'text-white/90' : 'text-gray-600'}`}>
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-green-500'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => navigate(`/auth?plan=${plan.id}`)}
                  className={`w-full py-3 rounded-full font-semibold transition-all ${
                    plan.popular 
                      ? 'bg-white text-orange-600 hover:bg-gray-100' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Join 1 Lac+ humans across India on HumanOnRent.AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/auth?role=hirer')}
              className="bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-xl"
              data-testid="cta-rent-human"
            >
              Rent a Human
            </button>
            <button 
              onClick={() => navigate('/auth?role=provider')}
              className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
              data-testid="cta-rent-yourself"
            >
              Rent Yourself & Earn
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
